import React, {
  useState,
  useCallback,
  memo,
  lazy,
  Suspense,
  useMemo,
} from 'react';
import {
  Box,
  Tabs,
  Tab,
  Container,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Stack,
  IconButton,
  Tooltip,
  Badge,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  List as ListIcon,
  Assignment as TimesheetIcon,
  Build as CorrectionsIcon,
} from '@mui/icons-material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { attendanceKeys } from './hooks/queryKeys';

// Lazy load tab components
const OverviewTab = lazy(() => import('./OverviewTab.jsx'));
const AttendanceList = lazy(() => import('./AttendanceList.jsx'));
const TimesheetView = lazy(() => import('./TimesheetView.jsx'));
const CorrectionsTab = lazy(() => import('./CorrectionsTab.jsx'));

// -------- Loading & Error UI ------------------------------------------------

const TabLoadingSpinner = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 220,
    }}
  >
    <CircularProgress />
    <Typography variant="body2" color="text.secondary">
      Loading module...
    </Typography>
  </Box>
);

class TabErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, err: null };
  }
  static getDerivedStateFromError(err) {
    return { hasError: true, err };
  }
  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" color="error" gutterBottom>
            Something went wrong loading this tab.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {String(this.state.err?.message || 'Unknown error')}
          </Typography>
          <IconButton
            onClick={() => this.setState({ hasError: false, err: null })}
            color="primary"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      );
    }
    return this.props.children;
  }
}

// -------- Tab Panel ---------------------------------------------------------

const TabPanel = memo(({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`attendance-tabpanel-${index}`}
    aria-labelledby={`attendance-tab-${index}`}
    {...other}
    style={{ display: value === index ? 'block' : 'none' }}
  >
    <Box sx={{ py: 3 }}>{children}</Box>
  </div>
));

// -------- Tabs Config -------------------------------------------------------

const useTabsConfig = () =>
  useMemo(
    () => [
      { key: 'overview', label: 'Overview', icon: <DashboardIcon />, component: OverviewTab },
      { key: 'list', label: 'Records', icon: <ListIcon />, component: AttendanceList },
      { key: 'timesheet', label: 'Timesheet', icon: <TimesheetIcon />, component: TimesheetView },
      { key: 'corrections', label: 'Corrections', icon: <CorrectionsIcon />, component: CorrectionsTab },
    ],
    []
  );

// -------- Main Component ----------------------------------------------------

const ManageAttendance = memo(() => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const tabs = useTabsConfig();
  const queryClient = useQueryClient();

  // URL & localStorage tab persistence
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const storedKey = typeof window !== 'undefined' ? localStorage.getItem('attendance_last_tab_v2') : null;
  const defaultKey = tabs[0].key;
  const initialKey = tabs.some(t => t.key === tabParam)
    ? tabParam
    : tabs.some(t => t.key === storedKey)
    ? storedKey
    : defaultKey;
  const initialIndex = Math.max(0, tabs.findIndex(t => t.key === initialKey));

  const [tabValue, setTabValue] = useState(initialIndex);
  const [loadedTabs, setLoadedTabs] = useState(new Set([initialIndex])); // Track loaded tabs
  const [tabBadges, setTabBadges] = useState({}); // { key: number }

  const activeTab = tabs[tabValue];

  const setBadge = useCallback((key, count) => {
    const normalizedCount =
      typeof count === 'number' && Number.isFinite(count) && count > 0 ? count : 0;

    setTabBadges((prev) => {
      const prevCount = prev[key] || 0;
      if (prevCount === normalizedCount) {
        return prev;
      }

      if (!normalizedCount) {
        if (!(key in prev)) {
          return prev;
        }

        const next = { ...prev };
        delete next[key];
        return next;
      }

      return { ...prev, [key]: normalizedCount };
    });
  }, []);

  const badgeHandlers = useMemo(
    () =>
      Object.fromEntries(
        tabs.map((tab) => [tab.key, (count) => setBadge(tab.key, count)])
      ),
    [setBadge, tabs]
  );

  const handleTabChange = useCallback(
    (event, newValue) => {
      setTabValue(newValue);
      setLoadedTabs(prev => new Set([...prev, newValue]));
      const key = tabs[newValue].key;
      setSearchParams(prev => {
        const p = new URLSearchParams(prev);
        p.set('tab', key);
        return p;
      }, { replace: true });
      localStorage.setItem('attendance_last_tab_v2', key);
    },
    [setSearchParams, tabs]
  );

  const handleRequestNavigate = useCallback(
    (targetKey, nextParams = {}) => {
      const nextIndex = tabs.findIndex((tab) => tab.key === targetKey);
      if (nextIndex < 0) {
        return;
      }

      setTabValue(nextIndex);
      setLoadedTabs((prev) => new Set([...prev, nextIndex]));

      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('tab', targetKey);

        Object.entries(nextParams || {}).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            params.delete(key);
            return;
          }

          params.set(key, String(value));
        });

        return params;
      }, { replace: true });

      localStorage.setItem('attendance_last_tab_v2', targetKey);
    },
    [setSearchParams, tabs]
  );

  // Prefetch next tab on hover for instant feel
  const handleTabHover = (idx) => {
    setLoadedTabs(prev => (prev.has(idx) ? prev : new Set([...prev, idx])));
  };

  // Global refresh event for children to react to
  const triggerGlobalRefresh = useCallback(() => {
    // Every attendance tab is on react-query now; one invalidate refreshes them all.
    queryClient.invalidateQueries({ queryKey: attendanceKeys.all });
  }, [queryClient]);

  // Dynamic page subtitle
  const subtitle = useMemo(() => {
    switch (activeTab?.key) {
      case 'overview':
        return 'On-time rate, status mix, and what needs review';
      case 'list':
        return 'Search, filter, and manage daily attendance records';
      case 'timesheet':
        return 'Per-employee work hours and exports';
      case 'corrections':
        return 'Inspect a day and add or correct a punch';
      default:
        return 'Workforce attendance';
    }
  }, [activeTab?.key]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: { xs: 3, md: 4 },
        bgcolor: 'grey.50',
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 600, letterSpacing: -0.3, color: 'text.primary' }}
            >
              Attendance
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {subtitle}
            </Typography>
          </Box>

          <Tooltip title="Refresh active tab">
            <span>
              <IconButton onClick={triggerGlobalRefresh} aria-label="Refresh active tab" sx={{ color: 'text.secondary' }}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

      {/* Main surface */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Tabs Navigation */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            borderBottom: 1,
            borderColor: 'rgba(226, 232, 240, 0.9)',
            bgcolor: '#ffffff',
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            aria-label="attendance tabs"
            sx={{
              px: 1,
              minHeight: 48,
              '& .MuiTab-root': {
                minHeight: 48,
                textTransform: 'none',
                fontSize: '0.9rem',
                fontWeight: 500,
                color: 'text.secondary',
              },
              '& .MuiTab-root.Mui-selected': {
                color: 'primary.main',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
                height: 2,
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={tab.key}
                label={isMobile ? '' : tab.label}
                icon={
                  <Badge
                    color="error"
                    overlap="circular"
                    badgeContent={tabBadges[tab.key] || 0}
                    invisible={!tabBadges[tab.key]}
                  >
                    {tab.icon}
                  </Badge>
                }
                iconPosition={isMobile ? 'top' : 'start'}
                id={`attendance-tab-${index}`}
                aria-controls={`attendance-tabpanel-${index}`}
                sx={{
                  gap: 1,
                  '& .MuiBadge-badge': {
                    boxShadow: '0 0 0 2px #ffffff',
                  },
                }}
                onMouseEnter={() => handleTabHover(index)}
              />
            ))}
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ minHeight: '70vh' }}>
          {tabs.map((tab, index) => {
            const isActive = tabValue === index;
            const shouldMount = loadedTabs.has(index);
            return (
              <TabPanel key={tab.key} value={tabValue} index={index}>
                <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
                  {shouldMount ? (
                    <TabErrorBoundary>
                      <Suspense fallback={<TabLoadingSpinner />}>
                        {React.createElement(tab.component, {
                          // Children can call these to enhance UX:
                          active: isActive,
                          tabKey: tab.key,
                          onSetBadge: badgeHandlers[tab.key],
                          onRequestNavigate: handleRequestNavigate,
                        })}
                      </Suspense>
                    </TabErrorBoundary>
                  ) : null}
                </Box>
              </TabPanel>
            );
          })}
        </Box>

        <Divider />

        {/* Footer hint */}
        <Box
          sx={{
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            color: 'text.secondary',
            bgcolor: '#f8fafc',
            borderTop: '1px solid rgba(226, 232, 240, 0.9)',
          }}
        >
          <Typography variant="caption">
            Tip: Hover a tab to pre-load it. Use the refresh button to reload the active tab.
          </Typography>
          <Typography variant="caption">
            URL aware: share with <code>?tab=list</code>, <code>?tab=corrections&amp;employee_code=10462&amp;date=2026-04-12</code>, etc.
          </Typography>
        </Box>
      </Paper>
    </Container>
  </Box>
  );
});

export default ManageAttendance;
